import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { useLang } from "@/i18n/LanguageContext";
import {
  CHAPTER_ORDER,
  CHAPTER_FIELDS,
  CHAPTER_LABELS_FR,
  CHAPTER_PURPOSE_FR,
  CHAPTER_PURPOSE_EN,
  migrateChapter,
} from "@/data/projectChapters";

// -----------------------------------------------------------------------------
// Maison Dodji — Architectural Brief (print-only view).
//
// Route: /projet/:token/brief
// This page is the official Maison Dodji Architectural Brief. Sober typography,
// no web UI chrome, no navigation, no pricing. Designed to be exported via the
// browser's print dialog to a PDF.
// -----------------------------------------------------------------------------

function editorialDate(iso, lang) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// Compact project number derived from the opaque token: MD-XXXXXX (uppercase).
function projectNumber(token) {
  if (!token) return "MD";
  return "MD-" + String(token).replace(/[^a-zA-Z0-9]/g, "").slice(0, 6).toUpperCase();
}

export default function ProjectBrief() {
  const { token } = useParams();
  const { lang } = useLang();
  const [space, setSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    document.title = "Brief architectural — Maison Dodji";
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => document.head.removeChild(m);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/space/${token}`);
        if (!cancelled) {
          if (r.ok) setSpace(await r.json());
          else setNotFound(true);
        }
      } catch { if (!cancelled) setNotFound(true); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) return null;
  if (notFound || !space) {
    return (
      <div className="brief-page min-h-screen flex items-center justify-center">
        <p className="brief-body">Brief indisponible.</p>
      </div>
    );
  }

  const briefLang = space.language === "en" ? "en" : "fr";
  const L = briefLang === "en" ? EN : FR;
  const purposes = briefLang === "en" ? CHAPTER_PURPOSE_EN : CHAPTER_PURPOSE_FR;
  const chapters = (space.chapters || [])
    .map(migrateChapter)
    .sort((a, b) => CHAPTER_ORDER.indexOf(a.type) - CHAPTER_ORDER.indexOf(b.type));

  const pn = projectNumber(space.token || token);

  return (
    <div className="brief-page" data-testid="project-brief">
      <BriefStyles />

      <div className="brief-toolbar no-print">
        <button
          type="button"
          onClick={() => window.print()}
          className="brief-print-btn"
          data-testid="brief-print"
        >
          {L.print}
        </button>
      </div>

      {/* Cover */}
      <section className="brief-cover">
        <p className="brief-eyebrow">{L.eyebrow}</p>
        <h1 className="brief-cover-title">{space.projectTitle}</h1>
        <p className="brief-cover-owner">{space.namedParty}</p>

        <dl className="brief-meta">
          <MetaRow k={L.projectNumber} v={pn} />
          <MetaRow k={L.client} v={space.namedParty} />
          <MetaRow k={L.title} v={space.projectTitle} />
          <MetaRow k={L.date} v={editorialDate(space.openedOn, briefLang)} />
          {space.pieceName && <MetaRow k={L.piece} v={space.pieceName} />}
          {space.pieceNumber && <MetaRow k={L.pieceNumber} v={space.pieceNumber} />}
        </dl>

        <p className="brief-confidential">{L.confidential}</p>
      </section>

      {/* Chapters */}
      {chapters.map((chap, i) => (
        <BriefChapter key={i} chap={chap} lang={briefLang} purpose={purposes[chap.type]} L={L} />
      ))}

      <footer className="brief-footer">
        <p className="brief-eyebrow">Maison Dodji</p>
        <p className="brief-footer-line">{pn} · {editorialDate(space.openedOn, briefLang)}</p>
      </footer>
    </div>
  );
}

function MetaRow({ k, v }) {
  return (
    <div className="brief-meta-row">
      <dt>{k}</dt>
      <dd>{v || "—"}</dd>
    </div>
  );
}

function BriefChapter({ chap, lang, purpose, L }) {
  const spec = CHAPTER_FIELDS[chap.type] || [];
  const fields = chap.fields || {};
  const labels = lang === "en" ? EN.fields : FR.fields;
  const titles = lang === "en" ? EN.chapterTitles : CHAPTER_LABELS_FR;

  // Skip empty draft chapters entirely from the printed brief.
  const hasContent =
    spec.some((f) => {
      const v = fields[f.key];
      if (f.kind === "list") return Array.isArray(v) && v.length > 0;
      return !!v;
    }) || !!chap.observations;
  if (!hasContent) return null;

  return (
    <section className="brief-chapter" data-testid={`brief-chapter-${chap.type}`}>
      <p className="brief-eyebrow">{L.chapterEyebrow}</p>
      <h2 className="brief-chapter-title">{titles[chap.type]}</h2>
      {purpose && <p className="brief-purpose">{purpose}</p>}

      <div className="brief-fields">
        {spec.map((f) => {
          const v = fields[f.key];
          if (f.kind === "list") {
            const list = Array.isArray(v) ? v : [];
            if (list.length === 0) return null;
            return <BriefList key={f.key} f={f} list={list} labels={labels} />;
          }
          if (!v) return null;
          if (f.kind === "select") {
            const opt = f.options.find((o) => o.v === v);
            return <BriefScalar key={f.key} label={labels[f.labelKey]} value={opt ? labels[opt.labelKey] : v} />;
          }
          if (f.kind === "textarea") {
            return <BriefProse key={f.key} label={labels[f.labelKey]} text={v} />;
          }
          return <BriefScalar key={f.key} label={labels[f.labelKey]} value={v} />;
        })}
      </div>

      {chap.observations && (
        <div className="brief-observations">
          <p className="brief-eyebrow">{labels.observations}</p>
          <p className="brief-observations-body">{chap.observations}</p>
        </div>
      )}
    </section>
  );
}

function BriefScalar({ label, value }) {
  return (
    <div className="brief-row">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function BriefProse({ label, text }) {
  const paragraphs = String(text).split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);
  return (
    <div className="brief-prose-block">
      <p className="brief-prose-label">{label}</p>
      {paragraphs.map((p, i) => <p key={i} className="brief-prose">{p}</p>)}
    </div>
  );
}

function BriefList({ f, list, labels }) {
  const isImageList = f.itemFields.some((s) => s.key === "imageUrl");
  if (isImageList) {
    return (
      <div className="brief-image-list">
        {list.map((item, i) => (
          item.imageUrl ? (
            <figure key={i} className="brief-figure">
              <img src={item.imageUrl} alt={item.caption || ""} />
              {item.caption && <figcaption>{item.caption}</figcaption>}
            </figure>
          ) : null
        ))}
      </div>
    );
  }
  return (
    <ul className="brief-item-list">
      {list.map((item, i) => (
        <li key={i} className="brief-item">
          {f.itemFields.map((sub) => (
            item[sub.key] ? (
              <div key={sub.key} className="brief-item-row">
                <span className="brief-item-label">{labels[sub.labelKey]}</span>
                <span className="brief-item-value">{item[sub.key]}</span>
              </div>
            ) : null
          ))}
        </li>
      ))}
    </ul>
  );
}

const FR = {
  eyebrow: "Maison Dodji — Brief architectural",
  confidential:
    "Document confidentiel — Établi par la Maison Dodji dans le cadre d'un Project Space privé.",
  projectNumber: "Numéro de projet",
  client: "Commanditaire",
  title: "Titre du projet",
  date: "Date d'ouverture",
  piece: "Pièce",
  pieceNumber: "Numéro de pièce",
  chapterEyebrow: "Chapitre",
  print: "Imprimer / Exporter en PDF",
  fields: {
    body: "Introduction", fullName: "Nom", city: "Ville", contact: "Contact",
    background: "Parcours", address: "Adresse", style: "Style", era: "Époque",
    rooms: "Pièces principales", atmosphere: "Atmosphère",
    volumes: "Volumes", materials: "Matériaux", light: "Lumière",
    ambiance: "Ambiance",
    summary: "Résumé", brand: "Marque", reference: "Référence",
    notes: "Notes", intent: "Intention", horizon: "Horizon",
    hasSafe: "Coffre existant", model: "Modèle",
    capacity: "Capacité", location: "Emplacement",
    limitations: "Limitations",
    rhythm: "Rythme de vie", travel: "Voyages", interests: "Centres d'intérêt",
    imageUrl: "Image", caption: "Légende",
    name: "Nom", agency: "Agence",
    label: "Zone", dimensions: "Dimensions",
    access: "Accès", electrical: "Électricité",
    humidity: "Hygrométrie", security: "Sécurité", other: "Autres",
    prompt: "Question", nextSteps: "Prochaines étapes",
    observations: "Observations de la Maison",
    yes: "Oui", no: "Non", na: "Sans objet",
  },
};

const EN = {
  eyebrow: "Maison Dodji — Architectural Brief",
  confidential:
    "Confidential document — Prepared by Maison Dodji as part of a private Project Space.",
  projectNumber: "Project number",
  client: "Client",
  title: "Project title",
  date: "Opening date",
  piece: "Piece",
  pieceNumber: "Piece number",
  chapterEyebrow: "Chapter",
  print: "Print / Export as PDF",
  chapterTitles: {
    welcome: "Welcome to the Project Space",
    projectOwner: "The Project Owner",
    residence: "The Residence",
    architecture: "Architecture",
    watchCollection: "Watch Collection",
    existingSafe: "Existing Safe",
    futureCollection: "Future Collection",
    lifestyle: "Lifestyle",
    designReferences: "Design References",
    architect: "The Architect",
    interiorDesigner: "The Interior Designer",
    photos: "Photographs of the Place",
    measurements: "Measurements and Dimensions",
    technicalConstraints: "Technical Constraints",
    maisonObservations: "Observations from the Maison",
    maisonQuestions: "Questions from the Maison",
    projectConclusion: "Project Conclusion",
  },
  fields: {
    body: "Introduction", fullName: "Name", city: "City", contact: "Contact",
    background: "Background", address: "Address", style: "Style", era: "Era",
    rooms: "Principal rooms", atmosphere: "Atmosphere",
    volumes: "Volumes", materials: "Materials", light: "Light",
    ambiance: "Ambiance",
    summary: "Summary", brand: "Brand", reference: "Reference",
    notes: "Notes", intent: "Intent", horizon: "Horizon",
    hasSafe: "Existing safe", model: "Model",
    capacity: "Capacity", location: "Location",
    limitations: "Limitations",
    rhythm: "Rhythm of life", travel: "Travel", interests: "Interests",
    imageUrl: "Image", caption: "Caption",
    name: "Name", agency: "Studio",
    label: "Area", dimensions: "Dimensions",
    access: "Access", electrical: "Electrical",
    humidity: "Humidity", security: "Security", other: "Other",
    prompt: "Question", nextSteps: "Next steps",
    observations: "Observations from the Maison",
    yes: "Yes", no: "No", na: "Not applicable",
  },
};

// Print-friendly styles scoped to the brief route.
function BriefStyles() {
  return (
    <style>{`
      .brief-page {
        background: #F6F2EA;
        color: #1B1917;
        font-family: 'EB Garamond', Georgia, serif;
        padding: 56px 72px;
        max-width: 900px;
        margin: 0 auto;
        line-height: 1.6;
      }
      .brief-eyebrow {
        font-family: Inter, system-ui, sans-serif;
        font-size: 11px;
        letter-spacing: 0.22em;
        text-transform: uppercase;
        color: #6B655C;
        margin-bottom: 8px;
      }
      .brief-cover {
        min-height: 90vh;
        padding-top: 40px;
        page-break-after: always;
        display: flex;
        flex-direction: column;
      }
      .brief-cover-title {
        font-family: 'EB Garamond', Georgia, serif;
        font-size: 44px;
        line-height: 1.1;
        font-weight: 400;
        margin: 24px 0 16px;
      }
      .brief-cover-owner {
        font-family: 'EB Garamond', Georgia, serif;
        font-style: italic;
        font-size: 20px;
        color: #4A453E;
        margin-bottom: 64px;
      }
      .brief-meta {
        margin-top: auto;
        border-top: 1px solid #C7BFB1;
        padding-top: 24px;
      }
      .brief-meta-row {
        display: grid;
        grid-template-columns: 200px 1fr;
        gap: 24px;
        padding: 8px 0;
        border-bottom: 1px dotted #C7BFB1;
      }
      .brief-meta-row dt {
        font-family: Inter, system-ui, sans-serif;
        font-size: 11px;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: #6B655C;
      }
      .brief-meta-row dd {
        font-family: 'EB Garamond', Georgia, serif;
        font-size: 15px;
        color: #1B1917;
        margin: 0;
      }
      .brief-confidential {
        margin-top: 40px;
        font-family: Inter, system-ui, sans-serif;
        font-size: 11px;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: #6B655C;
      }
      .brief-chapter {
        page-break-inside: avoid;
        padding: 48px 0 32px;
        border-top: 1px solid #C7BFB1;
      }
      .brief-chapter-title {
        font-family: 'EB Garamond', Georgia, serif;
        font-size: 28px;
        line-height: 1.2;
        font-weight: 400;
        margin: 6px 0 12px;
      }
      .brief-purpose {
        font-family: 'EB Garamond', Georgia, serif;
        font-style: italic;
        font-size: 15px;
        color: #4A453E;
        margin-bottom: 28px;
      }
      .brief-fields { display: grid; gap: 18px; }
      .brief-row {
        display: grid;
        grid-template-columns: 200px 1fr;
        gap: 24px;
        padding: 6px 0;
      }
      .brief-row dt {
        font-family: Inter, system-ui, sans-serif;
        font-size: 11px;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: #6B655C;
      }
      .brief-row dd {
        font-family: 'EB Garamond', Georgia, serif;
        font-size: 15px;
        color: #1B1917;
        margin: 0;
      }
      .brief-prose-block { margin-top: 4px; }
      .brief-prose-label {
        font-family: Inter, system-ui, sans-serif;
        font-size: 11px;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: #6B655C;
        margin-bottom: 8px;
      }
      .brief-prose {
        font-family: 'EB Garamond', Georgia, serif;
        font-size: 15.5px;
        line-height: 1.75;
        color: #1B1917;
        margin: 0 0 8px;
        white-space: pre-line;
      }
      .brief-item-list {
        list-style: none;
        padding: 0;
        display: grid;
        gap: 12px;
      }
      .brief-item {
        border-left: 1px solid #C7BFB1;
        padding: 4px 0 4px 16px;
      }
      .brief-item-row { display: flex; gap: 12px; align-items: baseline; }
      .brief-item-label {
        font-family: Inter, system-ui, sans-serif;
        font-size: 10px;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        color: #6B655C;
        min-width: 90px;
      }
      .brief-item-value {
        font-family: 'EB Garamond', Georgia, serif;
        font-size: 15px;
        color: #1B1917;
      }
      .brief-image-list {
        display: grid;
        gap: 24px;
      }
      .brief-figure {
        margin: 0;
      }
      .brief-figure img {
        width: 100%;
        height: auto;
        border: 1px solid #C7BFB1;
      }
      .brief-figure figcaption {
        font-family: Inter, system-ui, sans-serif;
        font-size: 12px;
        color: #6B655C;
        margin-top: 6px;
      }
      .brief-observations {
        margin-top: 24px;
        padding-top: 16px;
        border-top: 1px dotted #C7BFB1;
      }
      .brief-observations-body {
        font-family: 'EB Garamond', Georgia, serif;
        font-style: italic;
        font-size: 15.5px;
        line-height: 1.75;
        color: #1B1917;
        white-space: pre-line;
      }
      .brief-footer {
        margin-top: 64px;
        padding-top: 24px;
        border-top: 1px solid #C7BFB1;
        text-align: center;
      }
      .brief-footer-line {
        font-family: Inter, system-ui, sans-serif;
        font-size: 11px;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        color: #6B655C;
      }
      .brief-toolbar {
        position: fixed;
        top: 16px;
        right: 16px;
        z-index: 50;
      }
      .brief-print-btn {
        font-family: Inter, system-ui, sans-serif;
        font-size: 11px;
        letter-spacing: 0.22em;
        text-transform: uppercase;
        color: #1B1917;
        background: #F6F2EA;
        border: 1px solid #1B1917;
        padding: 12px 20px;
        cursor: pointer;
      }
      .brief-body {
        font-family: 'EB Garamond', Georgia, serif;
        font-size: 16px;
        color: #4A453E;
      }
      @media print {
        html, body { background: #ffffff !important; }
        .no-print { display: none !important; }
        .brief-page { padding: 0; max-width: none; background: #ffffff; }
        .brief-cover { min-height: auto; padding-top: 0; }
        .brief-chapter { page-break-inside: avoid; }
      }
    `}</style>
  );
}
